import { EyeIcon, PencilIcon } from "@heroicons/react/outline";
import Button from "components/button"
import Input from "components/input";
import { SwitchButton } from "components/switchButton";
import { Tag } from "components/tag";
import { ISportParticipantItem } from "interfaces"
import { Link } from "react-router-dom"
import { isHaveActionPermission } from "utills/helpers";

interface ParticipantCardProps {
  data?: ISportParticipantItem[];
  pageSize: number;
  currentPage: number;
  _undoCheckParticipantId: (id: string) => void;
  _checkParticipantId:(id: string) => void;
  _toggleActive: (id: string, isActive: boolean) => void;
  participantIds?: string[] | null;
}

export const ParticipantCard = ({data = [], _checkParticipantId, _undoCheckParticipantId, _toggleActive, participantIds, currentPage, pageSize }: ParticipantCardProps) => {

  return (
    <ul className="bg-white">
      {data.map((participant: ISportParticipantItem, index: number) => (
        <div key={index} className="flex flex-col gap-y-4 overflow-hidden shadow-md ring-1 ring-gray-900/5 [&:not(:last-child)]:mb-4 p-4 rounded-xl">
          <li className="relative flex items-start justify-between gap-x-6">
            <div>
              <Input
                type="checkbox"
                disabled={
                  !isHaveActionPermission(
                    "sports",
                    "participants",
                    "export"
                  ) &&
                  !isHaveActionPermission("sports", "participants", "edit")
                }
                checked={(participantIds ?? []).includes(participant.participant_id)} 
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (e.target.checked) {
                    _checkParticipantId(participant.participant_id);
                  } else {
                    _undoCheckParticipantId(participant.participant_id);
                  }
                }}
              />
            </div>
            <div className="flex flex-row items-center gap-x-2">
              <Link to={`/participants/sport/${participant.participant_id}`} className="w-fit">
                <span className="sr-only">View Condition</span>
                <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                  <EyeIcon className="w-4 h-4 text-black" />
                </Button>
              </Link>
              {isHaveActionPermission("sports", "participants", "edit") ? (
                <Link to={`/participants/sport/${participant.participant_id}/edit`} className="w-fit">
                  <span className="sr-only">Edit Condition</span>
                  <Button className="w-8 h-8 bg-gray-200 hover:bg-gray-100 active:bg-gray-300">
                    <PencilIcon className="w-4 h-4 text-black" />
                  </Button>
                </Link>
              ) : <></>}
            </div>
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              No
            </p>
            <p className="flex-1 text-right text-sm leading-6">
              {(currentPage - 1) * pageSize + 1 + index}
            </p>
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              Source
            </p>
            <Tag text={participant.source} type="blue" customClassName="capitalize" />
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              Status
            </p>
            <SwitchButton
              checked={participant.is_active}
              disabled={!isHaveActionPermission("sports", "participants", "edit")}
              onChange={() => _toggleActive(participant.participant_id, !participant.is_active)}
            />
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              Ewaiver Status
            </p>
            <Tag text={participant.online_checked ? "Signed" : "Unsigned"} type={participant.online_checked ? "green" : "pink"} />
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
             Participant ID
            </p>
            <p className="flex-1 text-right text-sm leading-6" style={{wordBreak: "break-word"}}>
              <Link to={`/participants/sport/${participant.participant_id}`} className="w-fit text-brand-500 hover:underline">
                {participant.participant_id}
              </Link>
            </p>
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              Full Name
            </p>
            <p className="flex-1 text-right text-sm leading-6">{participant.full_name}</p>
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              Category name
            </p>
            <p className="flex-1 text-right text-sm leading-6">{participant.category_name_en}</p>
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              BIB
            </p>
            <p className="flex-1 text-right text-sm leading-6">{participant.bib}</p>
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              Email
            </p>
            <p className="flex-1 text-right text-sm leading-6">{participant.email}</p>
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              Phone
            </p>
            <p className="flex-1 text-right text-sm leading-6">
              {participant.phone_area_code ? `(+${participant.phone_area_code})` : ""} {participant.phone}
            </p>
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              Email
            </p>
            <p className="flex-1 text-right text-sm leading-6">{participant.id_card}</p>
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              Order Code
            </p>
            <div className="flex-1 text-right text-sm leading-6">
              <Link to={`/orders/${participant.parent_ref_id}`} className="w-fit font-medium text-brand-500 hover:underline">
                {participant.parent_ref_id}
              </Link>
            </div>
          </li>
          <li className="relative flex items-center justify-between gap-x-6">
            <p className="flex-1 text-sm font-semibold leading-6">
              Event name
            </p>
            <p className="flex-1 text-right text-sm leading-6">{participant.event_name_en}</p>
          </li>
        </div>
      ))}
    </ul>
  )
}
